/* eslint-disable react/jsx-no-bind */
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonV2 } from 'ui/ButtonV2';
import { PayLinkButton } from '../ui/PayLinkButton';
import { PayBaseModal } from './PayBaseModal';
import { ViewBillContentSection } from 'components/bill/ui/ViewBill/ViewBillContentSection';
import { ViewBillOrderItem } from 'components/bill/ui/ViewBill/ViewBillOrderItem';
import { IOrderReadResourceV18, SplittableItem } from 'components/order/model/Order';
import { orderApi } from 'components/order/orderApi';
import { orderService } from 'components/order/orderService';
import { InnerPageLayoutContent } from 'lib/InnerPageLayout/InnerPageLayoutContent';
import { useAuth } from 'lib/useAuth';
import noop from 'lodash/noop';
import sortBy from 'lodash/sortBy';
import { useSnackbar } from 'notistack';
import logger from 'lib/logger';
import { useCurrencyString } from 'lib/useCurrencyString';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';

interface PayItemSplittingModalContentProps {
    onClose: () => void;
    orderDetails?: IOrderReadResourceV18;
    onSubmit: (ids: number[]) => void;
}

interface PayItemSplittingModalProps extends PayItemSplittingModalContentProps {
    open: boolean;
}

const PayItemSplittingModalContent: React.FC<React.PropsWithChildren<PayItemSplittingModalContentProps>> = ({
    onClose,
    onSubmit,
    orderDetails: initialOrderDetails
}) => {
    const { t } = useTranslation();

    const { user: currentUser } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const { logUserEvent } = useGAHelpers();
    const [loading, setLoading] = React.useState(false);
    const [orderDetails, setOrderDetails] = React.useState<IOrderReadResourceV18 | undefined>(
        initialOrderDetails
    );
    const getCurrencyString = useCurrencyString();
    const mappedItems = React.useMemo(() => {
        if (orderDetails && currentUser) {
            return orderService.getSplittableItemMap(orderDetails, currentUser);
        }

        return [];
    }, [currentUser, orderDetails]);

    const unpaidItems = React.useMemo(
        () =>
            mappedItems
                .filter(item => !item.isPaid)
                .sort((item1, item2) => {
                    if (currentUser) {
                        if (
                            item1.claimedBy?.userId === currentUser.id &&
                            item2.claimedBy?.userId !== currentUser.id
                        ) {
                            return -1;
                        }
                        if (
                            item1.claimedBy?.userId !== currentUser.id &&
                            item2.claimedBy?.userId === currentUser.id
                        ) {
                            return 1;
                        }
                        if (item1.claimedBy !== undefined && item2.claimedBy === undefined) {
                            return 1;
                        }
                        if (item1.claimedBy === undefined && item2.claimedBy !== undefined) {
                            return -1;
                        }
                    }
                    return 0;
                }),
        [currentUser, mappedItems]
    );
    const paidItems = React.useMemo(
        () =>
            sortBy(
                mappedItems.filter(item => item.isPaid),
                item => item.claimedBy?.userId
            ),
        [mappedItems]
    );
    const claimedItems = React.useMemo(
        () => unpaidItems.filter(item => item.claimedBy?.userId === currentUser?._id && !item.isPaid),
        [currentUser?._id, unpaidItems]
    );
    const hasClaimedItems = React.useMemo(() => claimedItems && claimedItems.length > 0, [claimedItems]);

    const handleClaimRequest = React.useCallback(
        async (itemIds: number[]) => {
            if (orderDetails?.id) {
                setLoading(true);
                try {
                    const response = await orderApi.claimItems(itemIds, orderDetails.id.toString());
                    setOrderDetails(response);
                } catch (err: any) {
                    if (err.status === 409) {
                        setOrderDetails(err.data);
                        enqueueSnackbar(t('ERROR_CLAIM_CLASH'), {
                            variant: 'error'
                        });
                    }
                }
                setLoading(false);
            }
        },
        [enqueueSnackbar, orderDetails?.id, t]
    );
    const handleClick = React.useCallback(
        (item: IItemReadResourceV10) => {
            const selectedItem = unpaidItems.find(({ id }) => id === item.id);
            const claimedItemIds = mappedItems
                .filter(mappedItem => mappedItem.claimedBy?.userId === currentUser?._id && !mappedItem.isPaid)
                .map(mappedItem => mappedItem.id);
            if (!selectedItem) {
                enqueueSnackbar(t('GENERAL_GENERIC_ERROR'), { variant: 'error' });
                return;
            }
            if (selectedItem.claimedBy?.userId === currentUser?._id) {
                const itemIndex = claimedItemIds.indexOf(selectedItem.id);

                if (itemIndex !== -1) {
                    claimedItemIds.splice(itemIndex, 1);
                }
            } else if (selectedItem.claimedBy === undefined) {
                claimedItemIds.push(selectedItem.id);
            }

            // Add condiment products to the list
            claimedItemIds.forEach(itemId => {
                const claimedItem = unpaidItems.find(({ id }) => id === itemId);

                if (claimedItem && claimedItem.condiments.length > 0) {
                    claimedItem.condiments.forEach(condiment => {
                        claimedItemIds.push(
                            ...Array(condiment.quantity / claimedItem.quantity).fill(condiment.id)
                        );
                    });
                }
            });

            handleClaimRequest(claimedItemIds);
        },
        [currentUser?._id, enqueueSnackbar, handleClaimRequest, mappedItems, t, unpaidItems]
    );
    const renderItem = React.useCallback(
        (item: SplittableItem, index: number) => {
            if (orderDetails) {
                return (
                    <ViewBillOrderItem
                        index={index}
                        showClaimedBy
                        user={item.claimedBy}
                        orderItem={item}
                        allItems={orderDetails?.items}
                        onClick={item.available ? handleClick : noop}
                        showPricePerOne
                        key={index}
                    />
                );
            }
            return null;
        },
        [handleClick, orderDetails]
    );
    const handleClaimAllRemaining = React.useCallback(() => {
        logUserEvent(EGAEventName.PatItemSplitClaimAllClick);
        const claimedItemIds = unpaidItems
            .filter(item => item.claimedBy?.userId === currentUser?._id || item.claimedBy === undefined)
            .map(item => item.id);

        // Add condiment products to the list
        claimedItemIds.forEach(itemId => {
            const claimedItem = unpaidItems.find(({ id }) => id === itemId);

            if (claimedItem && claimedItem.condiments.length > 0) {
                claimedItem.condiments.forEach(condiment => claimedItemIds.push(condiment.id));
            }
        });

        handleClaimRequest(claimedItemIds);
    }, [currentUser?._id, handleClaimRequest, logUserEvent, unpaidItems]);
    const handleClearAllClaims = React.useCallback(async () => {
        logUserEvent(EGAEventName.PatItemSplitClearAllClick);
        if (!orderDetails?.id) {
            return;
        }
        setLoading(true);
        try {
            const result = await orderApi.claimItems([], orderDetails?.id?.toString());
            setLoading(false);
            setOrderDetails(result);
            return result;
        } catch (e) {
            logger.error(e);
        }
        return null;
    }, [logUserEvent, orderDetails?.id]);
    const total = React.useMemo(
        () =>
            claimedItems.reduce((acc, item) => {
                acc += item.cost;

                if (item.condiments.length > 0) {
                    item.condiments.forEach(
                        condiment => (acc += (condiment.cost * condiment.quantity) / item.quantity)
                    );
                }

                if (item.modifiers && !!item.modifiers.length) {
                    item.modifiers.forEach(({ options }) => {
                        options.forEach(({ cost }) => (acc += cost));
                    });
                }
                return acc;
            }, 0),
        [claimedItems]
    );

    const handleCheckout = React.useCallback(() => {
        if (currentUser && orderDetails) {
            const items = orderService.getSplittableItemMap(orderDetails, currentUser);
            const claimedItemIds = orderService.getUserClaimedItemIds(items, currentUser);
            if (!claimedItemIds.length) {
                logUserEvent(EGAEventName.PatItemSplitConfirmEmptyClick);
            } else {
                logUserEvent(EGAEventName.PatItemSplitConfirmClick);
            }
            onSubmit(claimedItemIds);
        }
    }, [currentUser, logUserEvent, onSubmit, orderDetails]);
    const handleReset = React.useCallback(async () => {
        logUserEvent(EGAEventName.PatItemSplitPayFullClick);
        await handleClearAllClaims();
        onSubmit([]);
    }, [handleClearAllClaims, logUserEvent, onSubmit]);
    return (
        <Box display="flex" flexDirection="column" pt={1}>
            <Box flex={1}>
                <Typography sx={{ fontWeight: 700 }} align="center" variant="h6">
                    {t('BILL_SPLIT_BY_ITEM_TITLE')}
                </Typography>
                <Box display="flex" flex={1} justifyContent="space-between">
                    <PayLinkButton onClick={handleClaimAllRemaining}>
                        {t('TAB_CLAIM_ALL_BUTTON_TITLE')}
                    </PayLinkButton>
                    <PayLinkButton onClick={handleClearAllClaims}>
                        {t('TAB_CLAIM_NONE_BUTTON_TITLE')}
                    </PayLinkButton>
                </Box>
                <Box>
                    <InnerPageLayoutContent>
                        <ViewBillContentSection bottomSpacing={2}>
                            {unpaidItems.map(renderItem)}
                        </ViewBillContentSection>
                        {paidItems.length > 0 && (
                            <ViewBillContentSection bottomSpacing={2}>
                                <Typography align="center" variant="body1">
                                    {t('BILL_SPLIT_BY_ITEM_PAID_ITEMS_LABEL')}
                                </Typography>
                                {paidItems.map(renderItem)}
                            </ViewBillContentSection>
                        )}
                    </InnerPageLayoutContent>
                </Box>
            </Box>
            <Box>
                <ButtonV2 secondary onClick={onClose} disabled={loading}>
                    {t('GENERAL_CANCEL')}
                </ButtonV2>
                {hasClaimedItems && (
                    <ButtonV2 secondary onClick={handleReset} disabled={loading}>
                        {t('PAT_QUICKPAY_PAY_FULL')}
                    </ButtonV2>
                )}
                <ButtonV2
                    disabled={loading}
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={handleCheckout}
                >
                    {hasClaimedItems
                        ? t('BILL_SPLIT_BY_ITEM_PAY_BTN', {
                              amount: getCurrencyString(total)
                          })
                        : t('PAT_QUICKPAY_PAY_FULL')}
                </ButtonV2>
            </Box>
        </Box>
    );
};

export const PayItemSplittingModal: React.FC<React.PropsWithChildren<PayItemSplittingModalProps>> = ({
    open,
    ...rest
}) => {
    if (!open) {
        return null;
    }
    return (
        <PayBaseModal open={open} onClose={rest.onClose}>
            <PayItemSplittingModalContent {...rest} />
        </PayBaseModal>
    );
};
